import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Service } from "../../../types";
import {
  adminApi,
  aiApi,
  revenueTargetsApi,
  servicesApi,
} from "../../../services";
import type { RevenueTargetAiStrategy } from "../../../services/aiApi";
import { formatMonthYearLong } from "../../../utils/dateUtils";
import { ActionButton, ErrorState, Icons, Input, Loading } from "../../shared";
import { useToaster } from "../../shared/Toaster/Toaster";
import { formatBalanceCurrency } from "./balanceShared";

interface MonthlyRevenueTargetProgressProps {
  month: number;
  year: number;
}

interface ServiceSellSuggestion {
  serviceId: number;
  serviceName: string;
  unitPrice: number;
  unitsNeeded: number;
  projectedRevenue: number;
}

function formatAmountInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const number = Number(digits) / 100;
  return number.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseAmountInput(value: string): number {
  const normalized = value
    .replace(/\./g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
  return parseFloat(normalized) || 0;
}

function amountToInput(amount: number): string {
  return amount.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseServicePrice(service: Service): number {
  const base = parseFloat(String(service.price ?? 0));
  if (!Number.isNaN(base) && base > 0) return base;

  const effortPrices = [
    service.priceLight,
    service.priceMedium,
    service.priceHeavy,
  ]
    .map((value) => (value == null ? NaN : Number(value)))
    .filter((value) => !Number.isNaN(value) && value > 0);

  if (effortPrices.length === 0) return 0;
  return effortPrices.reduce((sum, value) => sum + value, 0) / effortPrices.length;
}

function buildSuggestions(
  gap: number,
  services: Service[]
): ServiceSellSuggestion[] {
  if (gap <= 0) return [];

  return services
    .map((service) => {
      const unitPrice = parseServicePrice(service);
      if (unitPrice <= 0) return null;
      const unitsNeeded = Math.ceil(gap / unitPrice);
      return {
        serviceId: service.id,
        serviceName: service.name,
        unitPrice,
        unitsNeeded,
        projectedRevenue: unitsNeeded * unitPrice,
      };
    })
    .filter((row): row is ServiceSellSuggestion => row != null)
    .sort((a, b) => {
      if (a.unitsNeeded !== b.unitsNeeded) {
        return a.unitsNeeded - b.unitsNeeded;
      }
      return a.unitPrice - b.unitPrice;
    })
    .slice(0, 5);
}

export const MonthlyRevenueTargetProgress: React.FC<
  MonthlyRevenueTargetProgressProps
> = ({ month, year }) => {
  const { showToast } = useToaster();
  const [targetAmount, setTargetAmount] = useState<number | null>(null);
  const [currentRevenue, setCurrentRevenue] = useState<number | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [amountInput, setAmountInput] = useState("");
  const [aiStrategies, setAiStrategies] = useState<RevenueTargetAiStrategy[]>(
    []
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAiStrategies([]);
    setAiError(null);

    try {
      const [targetRes, balanceRes, servicesRes] = await Promise.all([
        revenueTargetsApi.get(month, year),
        adminApi.getMonthlyBalance(month, year),
        servicesApi.getServicesByCompany(),
      ]);

      if (targetRes.success) {
        const amount = targetRes.data?.amount ?? null;
        setTargetAmount(amount);
        setAmountInput(amount != null ? amountToInput(amount) : "");
        setIsEditing(false);
      } else {
        setTargetAmount(null);
        setAmountInput("");
      }

      if (balanceRes.success && balanceRes.data) {
        setCurrentRevenue(balanceRes.data.total ?? 0);
      } else {
        setCurrentRevenue(0);
      }

      if (servicesRes.success && servicesRes.data) {
        setServices(servicesRes.data.filter((service) => service.isActive !== false));
      } else {
        setServices([]);
      }

      if (!targetRes.success && !balanceRes.success) {
        setError("Erro ao carregar meta e receita do mês");
      }
    } catch {
      setError("Erro ao carregar progresso da meta");
      setTargetAmount(null);
      setCurrentRevenue(null);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEditing = () => {
    setAmountInput(targetAmount != null ? amountToInput(targetAmount) : "");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setAmountInput(targetAmount != null ? amountToInput(targetAmount) : "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    const amount = parseAmountInput(amountInput);
    if (amount <= 0) {
      showToast("error", "Informe um valor válido para a meta");
      return;
    }

    setSaving(true);
    try {
      const res = await revenueTargetsApi.upsert({ month, year, amount });
      if (res.success && res.data) {
        setTargetAmount(Number(res.data.amount));
        setAmountInput(amountToInput(Number(res.data.amount)));
        setIsEditing(false);
        setAiStrategies([]);
        setAiError(null);
        showToast("success", "Meta de receita salva");
      } else {
        showToast("error", res.error || "Erro ao salvar meta");
      }
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Erro ao salvar meta"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      const res = await revenueTargetsApi.remove(month, year);
      if (res.success) {
        setTargetAmount(null);
        setAmountInput("");
        setIsEditing(false);
        setAiStrategies([]);
        setAiError(null);
        showToast("success", "Meta removida");
      } else {
        showToast("error", res.error || "Erro ao remover meta");
      }
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Erro ao remover meta"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAiSuggestions = async () => {
    if (targetAmount == null || currentRevenue == null) return;

    setAiLoading(true);
    setAiError(null);
    try {
      const res = await aiApi.generateRevenueTargetSuggestions({
        month,
        year,
        targetAmount,
        currentRevenue,
      });

      if (!res.success || !res.data?.strategies?.length) {
        throw new Error(
          res.error || "Não foi possível gerar sugestões com IA."
        );
      }

      setAiStrategies(res.data.strategies);
    } catch (e) {
      setAiStrategies([]);
      setAiError(
        e instanceof Error
          ? e.message
          : "Não foi possível gerar sugestões com IA."
      );
    } finally {
      setAiLoading(false);
    }
  };

  const gap =
    targetAmount != null && currentRevenue != null
      ? targetAmount - currentRevenue
      : null;
  const progressPercent =
    targetAmount != null && targetAmount > 0 && currentRevenue != null
      ? Math.min(100, Math.max(0, (currentRevenue / targetAmount) * 100))
      : null;

  const suggestions = useMemo(() => {
    if (gap == null || gap <= 0) return [];
    return buildSuggestions(gap, services);
  }, [gap, services]);

  const showEditor = isEditing || targetAmount == null;

  return (
    <div className="bg-white dark:!bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100">
            Meta de Receita
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            {formatMonthYearLong(month, year)}
          </p>
        </div>
        {!loading && !error && targetAmount != null && !isEditing && (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Icons.Edit sx={{ fontSize: 16 }} />
            Editar meta
          </button>
        )}
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchData} />
      ) : (
        <div className="space-y-5">
          {showEditor && (
            <div className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/50 p-4 space-y-4">
              <Input
                label="Meta de receita do mês"
                value={amountInput}
                onChange={(e) => setAmountInput(formatAmountInput(e.target.value))}
                placeholder="0,00"
              />
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <ActionButton
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                  icon={<Icons.Save sx={{ fontSize: 18 }} />}
                  className="w-full sm:w-auto"
                >
                  Salvar meta
                </ActionButton>
                {targetAmount != null && (
                  <ActionButton
                    variant="outline"
                    size="sm"
                    onClick={cancelEditing}
                    disabled={saving}
                    className="w-full sm:w-auto"
                  >
                    Cancelar
                  </ActionButton>
                )}
                {targetAmount != null && (
                  <ActionButton
                    variant="outline"
                    size="sm"
                    onClick={handleRemove}
                    disabled={saving}
                    icon={<Icons.Delete sx={{ fontSize: 18 }} />}
                    className="w-full sm:w-auto"
                  >
                    Remover
                  </ActionButton>
                )}
              </div>
            </div>
          )}

          {targetAmount != null && !isEditing && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 p-3 sm:p-4">
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                    Meta
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {formatBalanceCurrency(targetAmount)}
                  </p>
                </div>
                <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3 sm:p-4">
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                    Receita atual
                  </p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {formatBalanceCurrency(currentRevenue ?? 0)}
                  </p>
                </div>
                <div
                  className={`rounded-xl border p-3 sm:p-4 ${
                    gap != null && gap <= 0
                      ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40"
                      : "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40"
                  }`}
                >
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">
                    {gap != null && gap <= 0
                      ? "Meta atingida"
                      : "Falta para a meta"}
                  </p>
                  <p
                    className={`text-lg sm:text-xl font-bold ${
                      gap != null && gap <= 0
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-amber-700 dark:text-amber-400"
                    }`}
                  >
                    {gap != null
                      ? formatBalanceCurrency(Math.abs(gap))
                      : "—"}
                  </p>
                </div>
              </div>

              {progressPercent != null && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-400 mb-1.5">
                    <span>Progresso</span>
                    <span className="font-medium text-gray-700 dark:text-slate-200">
                      {progressPercent.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        gap != null && gap <= 0 ? "bg-emerald-500" : "bg-sky-500"
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}

              {gap != null && gap > 0 && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100 mb-2">
                      Sugestões por serviço
                    </h4>
                    {suggestions.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Cadastre serviços com preço para gerar sugestões de venda.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {suggestions.map((suggestion) => (
                          <li
                            key={suggestion.serviceId}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 px-3 py-2.5"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {suggestion.serviceName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-slate-400">
                                {formatBalanceCurrency(suggestion.unitPrice)} por
                                atendimento
                              </p>
                            </div>
                            <div className="text-sm text-sky-700 dark:text-sky-400 font-semibold sm:text-right">
                              Vender {suggestion.unitsNeeded}{" "}
                              {suggestion.unitsNeeded === 1
                                ? "atendimento"
                                : "atendimentos"}
                              <span className="block text-xs font-normal text-gray-500 dark:text-slate-400">
                                ≈{" "}
                                {formatBalanceCurrency(suggestion.projectedRevenue)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                        Combinações com IA
                      </h4>
                      <button
                        type="button"
                        onClick={() => void handleGenerateAiSuggestions()}
                        disabled={aiLoading}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-sky-500 hover:bg-sky-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icons.Lightbulb sx={{ fontSize: 16 }} />
                        {aiLoading
                          ? "Gerando…"
                          : aiStrategies.length > 0
                            ? "Gerar de novo"
                            : "Sugerir com IA"}
                      </button>
                    </div>

                    {aiLoading && (
                      <Loading
                        message="Montando combinações de serviços…"
                        size="sm"
                        className="py-4"
                      />
                    )}

                    {!aiLoading && aiError && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 p-3">
                        <p className="text-amber-800 dark:text-amber-200 text-sm">
                          {aiError}
                        </p>
                      </div>
                    )}

                    {!aiLoading && !aiError && aiStrategies.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        Use a IA para sugerir mixes diferentes de serviços e
                        quantidades para fechar a meta.
                      </p>
                    )}

                    {!aiLoading && aiStrategies.length > 0 && (
                      <ul className="space-y-3">
                        {aiStrategies.map((strategy, index) => (
                          <li
                            key={`${strategy.title}-${index}`}
                            className="rounded-xl border border-sky-100 dark:border-sky-900/60 bg-sky-50/60 dark:bg-sky-950/20 px-3 py-3"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {strategy.title}
                                </p>
                                {strategy.summary ? (
                                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                                    {strategy.summary}
                                  </p>
                                ) : null}
                              </div>
                              <p className="text-sm font-semibold text-sky-700 dark:text-sky-400 sm:text-right shrink-0">
                                ≈ {formatBalanceCurrency(strategy.totalRevenue)}
                              </p>
                            </div>
                            <ul className="space-y-1.5">
                              {strategy.items.map((item) => (
                                <li
                                  key={`${strategy.title}-${item.serviceId}`}
                                  className="flex items-center justify-between gap-2 text-sm"
                                >
                                  <span className="text-gray-800 dark:text-slate-200 truncate">
                                    {item.units}× {item.serviceName}
                                  </span>
                                  <span className="text-gray-500 dark:text-slate-400 shrink-0">
                                    {formatBalanceCurrency(item.revenue)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {gap != null && gap <= 0 && (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
                  Parabéns! A receita do mês já alcançou ou superou a meta.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
